import React, { useState, useEffect, Fragment } from 'react';
import { toast } from 'react-toastify';
import { MessageSquare } from 'react-feather';
import api from '../../config/axios';
import Breadcrumb from '../common/breadcrumb';
import Loader from '../common/loader';
import { date, time } from '../../utils/date';

const BotLogs = (props) => {
    const [activeTab, setActiveTab] = useState('feed');
    const [loading, setLoading] = useState(false);
    const [logs, setLogs] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);

    // Filtro por fecha
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    // Buscar conversación
    const [searchPhone, setSearchPhone] = useState('');

    const limit = 50;

    useEffect(() => {
        if (activeTab === 'feed') fetchRecent(1);
    }, []);

    // ── Feed reciente ──
    const fetchRecent = async (pageNum) => {
        setLoading(true);
        try {
            const { data } = await api.get(
                `/whatsapp/logs/recent?limit=${limit}&page=${pageNum}`
            );
            setLogs(data.interactions || []);
            setTotal(data.total || 0);
            setTotalPages(data.totalPages || 1);
            setPage(pageNum);
        } catch (err) {
            toast.error('Error al cargar la bitácora');
        } finally {
            setLoading(false);
        }
    };

    // ── Filtro por fecha ──
    const fetchByDate = async (pageNum) => {
        if (!fromDate || !toDate) {
            toast.warn('Selecciona fecha inicial y final');
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.get(
                `/whatsapp/logs?from=${fromDate}&to=${toDate}&limit=${limit}&page=${pageNum}`
            );
            setLogs(data.interactions || []);
            setTotal(data.total || 0);
            setTotalPages(data.totalPages || 1);
            setPage(pageNum);
        } catch (err) {
            toast.error('Error al filtrar por fecha');
        } finally {
            setLoading(false);
        }
    };

    const handleDateFilter = (e) => {
        e.preventDefault();
        fetchByDate(1);
    };

    // ── Parsear content para mostrar texto legible ──
    const parseContent = (interaction) => {
        if (!interaction.content) return '-';
        // Outbound interactive = JSON stringificado
        if (interaction.direction === 'outbound' && interaction.messageType === 'interactive') {
            try {
                const parsed = JSON.parse(interaction.content);
                // { type: "button", body: { text: "..." }, ... } o { type: "list", body: {...}, ... }
                if (parsed.body && parsed.body.text) return parsed.body.text;
                return interaction.content.substring(0, 80) + '...';
            } catch {
                return interaction.content.substring(0, 80);
            }
        }
        return interaction.content;
    };

    // ── Agrupar por teléfono (para feed reciente) ──
    const groupByPhone = (interactions) => {
        const groups = {};
        interactions.forEach((item) => {
            if (!groups[item.phone]) {
                groups[item.phone] = {
                    phone: item.phone,
                    lastInteraction: item.createdAt,
                    count: 1,
                    lastItem: item,
                };
            } else {
                groups[item.phone].count++;
                if (new Date(item.createdAt) > new Date(groups[item.phone].lastInteraction)) {
                    groups[item.phone].lastInteraction = item.createdAt;
                    groups[item.phone].lastItem = item;
                }
            }
        });
        // Ordenar de más nuevo a más viejo por última interacción
        return Object.values(groups).sort(
            (a, b) => new Date(b.lastInteraction) - new Date(a.lastInteraction)
        );
    };

    // ── Navegar a conversación ──
    const goToConversation = (phone) => {
        props.history.push(`/bitacora-bot/conversacion/${phone}`);
    };

    // ── Cambiar de tab ──
    const switchTab = (tab) => {
        setActiveTab(tab);
        setLogs([]);
        setTotal(0);
        setTotalPages(1);
        setPage(1);
        if (tab === 'feed') fetchRecent(1);
    };

    // ── Badge de direction ──
    const directionBadge = (dir) => {
        if (dir === 'inbound') return <span className="badge badge-success">← Entrada</span>;
        return <span className="badge badge-info">→ Salida</span>;
    };

    // ── Badge de source ──
    const sourceBadge = (source) => {
        if (source === 'bot') return <span className="badge badge-secondary">Bot</span>;
        if (source === 'dashboard') return <span className="badge badge-warning">Dashboard</span>;
        return null;
    };

    // ── Paginación ──
    const handlePrev = () => {
        const prev = page - 1;
        if (prev >= 1) {
            if (activeTab === 'feed') fetchRecent(prev);
            else fetchByDate(prev);
        }
    };

    const handleNext = () => {
        const next = page + 1;
        if (next <= totalPages) {
            if (activeTab === 'feed') fetchRecent(next);
            else fetchByDate(next);
        }
    };

    return (
        <Fragment>
            <Breadcrumb title="🤖 Bitácora del Bot" />
            <div className="container-fluid">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                {/* Tabs */}
                                <ul className="nav nav-tabs mb-3">
                                    <li className="nav-item">
                                        <button
                                            className={`nav-link ${activeTab === 'feed' ? 'active' : ''}`}
                                            onClick={() => switchTab('feed')}
                                        >
                                            Feed Reciente
                                        </button>
                                    </li>
                                    <li className="nav-item">
                                        <button
                                            className={`nav-link ${activeTab === 'date' ? 'active' : ''}`}
                                            onClick={() => switchTab('date')}
                                        >
                                            Filtrar por fecha
                                        </button>
                                    </li>
                                    <li className="nav-item">
                                        <button
                                            className={`nav-link ${activeTab === 'search' ? 'active' : ''}`}
                                            onClick={() => switchTab('search')}
                                        >
                                            Buscar conversación
                                        </button>
                                    </li>
                                </ul>

                                {/* Tab: Buscar conversación */}
                                {activeTab === 'search' && (
                                    <div className="mb-4">
                                        <form
                                            className="form-inline"
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                if (searchPhone) goToConversation(searchPhone);
                                            }}
                                        >
                                            <div className="form-group mr-2">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Ej: 584121231127"
                                                    value={searchPhone}
                                                    onChange={(e) => setSearchPhone(e.target.value)}
                                                    style={{ width: 250 }}
                                                />
                                            </div>
                                            <button type="submit" className="btn btn-primary">
                                                <MessageSquare size={16} className="mr-1" />
                                                Ver conversación
                                            </button>
                                        </form>
                                    </div>
                                )}

                                {/* Tab: Filtrar por fecha */}
                                {activeTab === 'date' && (
                                    <form className="form-inline mb-3" onSubmit={handleDateFilter}>
                                        <div className="form-group mr-3">
                                            <label className="mr-2">Desde:</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={fromDate}
                                                onChange={(e) => setFromDate(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="form-group mr-3">
                                            <label className="mr-2">Hasta:</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={toDate}
                                                onChange={(e) => setToDate(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <button type="submit" className="btn btn-primary">
                                            Filtrar
                                        </button>
                                    </form>
                                )}

                                {/* Loading */}
                                {loading && (
                                    <div style={{ display: 'flex', height: '40vh' }}>
                                        <Loader />
                                    </div>
                                )}

                                {/* Sin datos */}
                                {!loading && logs.length === 0 && activeTab !== 'search' && (
                                    <div className="text-center py-5">
                                        <p style={{ fontSize: 18, color: '#777' }}>
                                            No hay interacciones registradas
                                        </p>
                                    </div>
                                )}

                                {/* Feed reciente — agrupado por teléfono */}
                                {!loading && logs.length > 0 && activeTab === 'feed' && (
                                    <Fragment>
                                        <div className="table-responsive">
                                            <table className="table table-bordered table-hover">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: 150 }}>Última actividad</th>
                                                        <th style={{ width: 140 }}>Teléfono</th>
                                                        <th style={{ width: 80 }}>Interacciones</th>
                                                        <th>Último mensaje</th>
                                                        <th style={{ width: 90 }}>Acción</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {groupByPhone(logs).map((group) => (
                                                        <tr
                                                            key={group.phone}
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => goToConversation(group.phone)}
                                                        >
                                                            <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                                                                {date(group.lastInteraction)}
                                                                <br />
                                                                <span className="text-muted">{time(group.lastInteraction)}</span>
                                                            </td>
                                                            <td><strong>{group.phone}</strong></td>
                                                            <td>
                                                                <span className="badge badge-primary">{group.count}</span>
                                                            </td>
                                                            <td style={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {group.lastItem.action && (
                                                                    <span className="badge badge-dark mr-1">
                                                                        {group.lastItem.action}
                                                                    </span>
                                                                )}
                                                                {parseContent(group.lastItem)}
                                                            </td>
                                                            <td>
                                                                <button
                                                                    className="btn btn-sm btn-outline-primary"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        goToConversation(group.phone);
                                                                    }}
                                                                >
                                                                    Ver
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Paginación */}
                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <span className="text-muted">
                                                {total} interacciones · Página {page} de {totalPages}
                                            </span>
                                            <div>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary mr-2"
                                                    onClick={handlePrev}
                                                    disabled={page <= 1}
                                                >
                                                    ← Anterior
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={handleNext}
                                                    disabled={page >= totalPages}
                                                >
                                                    Siguiente →
                                                </button>
                                            </div>
                                        </div>
                                    </Fragment>
                                )}

                                {/* Filtro por fecha — tabla plana */}
                                {!loading && logs.length > 0 && activeTab === 'date' && (
                                    <Fragment>
                                        <div className="table-responsive">
                                            <table className="table table-bordered table-hover">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: 150 }}>Fecha</th>
                                                        <th style={{ width: 140 }}>Teléfono</th>
                                                        <th style={{ width: 100 }}>Dir.</th>
                                                        <th style={{ width: 100 }}>Tipo</th>
                                                        <th style={{ width: 100 }}>Origen</th>
                                                        <th>Contenido</th>
                                                        <th style={{ width: 90 }}>Acción</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {logs.map((item) => (
                                                        <tr key={item.id} style={{ cursor: 'pointer' }} onClick={() => goToConversation(item.phone)}>
                                                            <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                                                                {date(item.createdAt)}
                                                                <br />
                                                                <span className="text-muted">{time(item.createdAt)}</span>
                                                            </td>
                                                            <td>{item.phone}</td>
                                                            <td>{directionBadge(item.direction)}</td>
                                                            <td><span className="badge badge-light">{item.messageType}</span></td>
                                                            <td>{sourceBadge(item.source)}</td>
                                                            <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {item.action && (
                                                                    <span className="badge badge-dark mr-1">
                                                                        {item.action}
                                                                    </span>
                                                                )}
                                                                {parseContent(item)}
                                                            </td>
                                                            <td>
                                                                <button
                                                                    className="btn btn-sm btn-outline-primary"
                                                                    onClick={(e) => { e.stopPropagation(); goToConversation(item.phone); }}
                                                                >
                                                                    Ver
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Paginación */}
                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <span className="text-muted">
                                                {total} interacciones · Página {page} de {totalPages}
                                            </span>
                                            <div>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary mr-2"
                                                    onClick={handlePrev}
                                                    disabled={page <= 1}
                                                >
                                                    ← Anterior
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={handleNext}
                                                    disabled={page >= totalPages}
                                                >
                                                    Siguiente →
                                                </button>
                                            </div>
                                        </div>
                                    </Fragment>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default BotLogs;
